// useAuth hook for the AiLys partner portal.
//
// Reads the session cookie + extracts JWT claims via parseJwtUnsafe.
// UI-cosmetic per ADR-0008; server-side verifyWorkOSJwt is authoritative
// at every protected edge function. This hook lets the UI branch on role
// without a server round trip.
//
// Phase 6.1 Chunk e.

import { useEffect, useState } from 'react';

// Signal cookie is the non-HttpOnly companion set by /api/auth/callback.
// Contains minimal JSON claims {role, partner_org_id, email, exp} for
// SPA routing. NOT authoritative; the real auth is the HttpOnly session
// cookie checked server-side at every edge function.
const SIGNAL_COOKIE = 'ailys_partner_signal';
const REFRESH_LEAD_TIME_SEC = 300; // refresh when 5 min remain on access token

export type Role =
  | 'client'
  | 'agent_in_training'
  | 'agent_certified'
  | 'partner_owner'
  | 'ailys_staff'
  | 'ailys_admin';

export interface AuthState {
  loading: boolean;
  authenticated: boolean;
  role: Role | null;
  partnerOrgId: string | null;
  email: string | null;
  features: string[];
  expiresAt: number; // unix timestamp (seconds), 0 if unknown
}

function readSignalCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const [k, v] = part.split('=').map((s) => s.trim());
    if (k === SIGNAL_COOKIE) return decodeURIComponent(v ?? '');
  }
  return null;
}

function parseSignalClaims(raw: string): {
  role: Role | null;
  partner_org_id: string | null;
  email: string | null;
  features: string[];
  exp: number;
} | null {
  try {
    const payload = JSON.parse(raw) as Record<string, unknown>;
    const role = typeof payload.role === 'string' ? (payload.role as Role) : null;
    const partner_org_id =
      typeof payload.partner_org_id === 'string' ? payload.partner_org_id : null;
    const email = typeof payload.email === 'string' ? payload.email : null;
    const features = Array.isArray(payload.features)
      ? payload.features.filter((f): f is string => typeof f === 'string')
      : [];
    const exp = typeof payload.exp === 'number' ? payload.exp : 0;
    return { role, partner_org_id, email, features, exp };
  } catch {
    return null;
  }
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    loading: true,
    authenticated: false,
    role: null,
    partnerOrgId: null,
    email: null,
    features: [],
    expiresAt: 0,
  });

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    function applySignal(token: string | null) {
      if (cancelled) return;
      if (!token) {
        setState({
          loading: false,
          authenticated: false,
          role: null,
          partnerOrgId: null,
          email: null,
          features: [],
          expiresAt: 0,
        });
        return;
      }
      const claims = parseSignalClaims(token);
      if (!claims || !claims.role) {
        setState({
          loading: false,
          authenticated: false,
          role: null,
          partnerOrgId: null,
          email: null,
          features: [],
          expiresAt: 0,
        });
        return;
      }
      setState({
        loading: false,
        authenticated: true,
        role: claims.role,
        partnerOrgId: claims.partner_org_id,
        email: claims.email,
        features: claims.features,
        expiresAt: claims.exp,
      });

      // Schedule refresh REFRESH_LEAD_TIME_SEC before exp.
      const now = Math.floor(Date.now() / 1000);
      const secondsUntilRefresh = Math.max(60, claims.exp - now - REFRESH_LEAD_TIME_SEC);
      refreshTimer = setTimeout(async () => {
        const refreshed = await tryRefresh();
        if (!refreshed) {
          // Refresh failed; cookie was cleared by /api/auth/refresh.
          // Force re-auth by re-reading session (now empty -> unauthenticated).
          applySignal(readSignalCookie());
          return;
        }
        // Refresh ok; new session cookie set. Re-read + re-schedule.
        applySignal(readSignalCookie());
      }, secondsUntilRefresh * 1000);
    }

    applySignal(readSignalCookie());
    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  return state;
}

const ROLE_RANK: Record<Role, number> = {
  client: 0,
  agent_in_training: 1,
  agent_certified: 2,
  partner_owner: 3,
  ailys_staff: 4,
  ailys_admin: 5,
};

export function hasRoleAtLeast(actual: Role | null, minimum: Role): boolean {
  if (!actual) return false;
  return ROLE_RANK[actual] >= ROLE_RANK[minimum];
}
